import React, { useEffect, useState } from 'react';
import CarrierHeader from '../../components/CarrierHeader';
import api from '../../utils/axiosInstance';

const AssignedDrivers = () => {
    const [drivers, setDrivers] = useState([]);
    const [filteredDrivers, setFilteredDrivers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    // Selected driver to show in modal
    const [selectedDriver, setSelectedDriver] = useState(null);

    // Toggle main modal
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Holds fetched resume data for the selected driver
    const [resume, setResume] = useState(null);

    // Toggle Add Favourite confirmation modal
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    // Feedback message after adding to favourites (success or already added)
    const [favouriteMessage, setFavouriteMessage] = useState('');

    useEffect(() => {
        const fetchDrivers = async () => {
            const stored = localStorage.getItem('carrierUser');
            if (!stored) return;
            const carrier = JSON.parse(stored);

            try {
                // 1. Check subscription
                const subRes = await api.get(`/subscribed/by-carrier/${carrier._id}`);

                const isActive = subRes.data?.active;

                if (!isActive) {
                    // No active subscription
                    setDrivers([]);
                    setFilteredDrivers([]);
                    setMessage('Subscribe a package to get drivers assigned');
                    setLoading(false);
                    return;
                }

                // 2. Subscription active → fetch carrier with drivers
                const res = await api.get(`/carriers/${carrier._id}`);
                const assignedDrivers = res.data?.carrier?.assignedDrivers || [];

                // Map assignedDrivers to driver objects
                const driverList = assignedDrivers
                    .filter((ad) => ad.driver !== null)
                    .map((ad) => ({
                        ...ad.driver,
                        assignedAt: ad.assignedAt,
                    }));

                setDrivers(driverList);
                setFilteredDrivers(driverList);
            } catch (error) {
                console.error('Error fetching drivers or subscription:', error);
                setMessage('Something went wrong while fetching drivers.');
            } finally {
                setLoading(false);
            }
        };

        fetchDrivers();
    }, []);

    useEffect(() => {
        if (!searchTerm) return setFilteredDrivers(drivers);

        const lower = searchTerm.toLowerCase();

        const result = drivers.filter((d) => {
            const assignedAt = d.assignedAt ? new Date(d.assignedAt).toLocaleDateString('en-GB') : '';

            return (
                d.name?.toLowerCase().includes(lower) ||
                d.city?.toLowerCase().includes(lower) ||
                d.state?.toLowerCase().includes(lower) ||
                assignedAt.includes(lower)
            );
        });

        setFilteredDrivers(result);
        setCurrentPage(1);
    }, [searchTerm, drivers]);

    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentDrivers = filteredDrivers.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredDrivers.length / itemsPerPage);

    // Opens modal, loads selected driver and fetches their resume
    const handleViewDriver = async (driver) => {
        setSelectedDriver(driver);
        setIsModalOpen(true);
        setResume(null); // clear previous driver's resume

        // Fetch resume for this specific driver
        if (driver?._id) {
            try {
                const res = await api.get(`/resumes/latest/${driver._id}`);
                if (res.data.success && res.data.resume) {
                    setResume(res.data.resume);
                } else {
                    setResume(null);
                }
            } catch (err) {
                console.error('Error fetching resume:', err.message);
                setResume(null);
            }
        }
        // navigate(`/carrier/driver-details/${driver._id}`, { state: { driver } });
    };
    // Closes modal and resets all related state
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedDriver(null);
        setResume(null);
        setIsConfirmOpen(false);
        setFavouriteMessage(''); //clear message on close
    };

    // Opens the Add Favourite confirmation sub-modal
    const handleOpenConfirm = () => setIsConfirmOpen(true);

    // Closes only the confirmation sub-modal, main modal stays open
    const handleCloseConfirm = () => setIsConfirmOpen(false);

    // Add driver to favourites, then closes all modals on success
    const handleConfirmAddFavourite = async () => {
        try {
            const carrier = JSON.parse(localStorage.getItem('carrierUser'));
            const res = await api.post('/interests', {
                carrierId: carrier._id,
                driverId: selectedDriver._id,
            });

            // Check if driver already exists in favourites
            if (res.data?.message === 'Already added' || res.data?.duplicate) {
                setFavouriteMessage('This driver is already in your favorites.');
            } else {
                setFavouriteMessage('Driver added to favorites!');
            }

            //Close confirm sub-modal, keep main modal open to show message
            setIsConfirmOpen(false);

            // Auto-clear message after 3 seconds
            setTimeout(() => setFavouriteMessage(''), 3000);

            // handleCloseModal();
        } catch (err) {
            console.error('Failed to add favourite:', err);

            // Handle duplicate if backend returns 409 conflict
            if (err.response?.status === 409) {
                setFavouriteMessage('This driver is already in your favorites.');
            } else {
                setFavouriteMessage('Something went wrong. Please try again.');
            }

            // Close confirm sub-modal and auto-clear message
            setIsConfirmOpen(false);
            setTimeout(() => setFavouriteMessage(''), 3000);
        }
    };

    // *** NEW: Pagination button rendering logic ***
    const renderPageButtons = () => {
        const pageButtons = [];
        const maxVisibleButtons = 5; // Show max 5 page buttons
        const halfVisible = Math.floor(maxVisibleButtons / 2);

        let startPage = Math.max(1, currentPage - halfVisible);
        let endPage = Math.min(totalPages, currentPage + halfVisible);

        // Adjust if we're near the beginning or end
        if (currentPage <= halfVisible) {
            endPage = Math.min(totalPages, maxVisibleButtons);
        }
        if (currentPage + halfVisible >= totalPages) {
            startPage = Math.max(1, totalPages - maxVisibleButtons + 1);
        }

        // First page button
        if (startPage > 1) {
            pageButtons.push(
                <button
                    key={1}
                    onClick={() => setCurrentPage(1)}
                    className='px-4 py-2 rounded-full border font-semibold bg-white text-primary border-primary hover:bg-gray-50'
                >
                    1
                </button>,
            );

            if (startPage > 2) {
                pageButtons.push(
                    <span key='ellipsis1' className='px-2 text-gray-500'>
                        ...
                    </span>,
                );
            }
        }

        // Middle page buttons
        for (let i = startPage; i <= endPage; i++) {
            pageButtons.push(
                <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`px-4 py-2 rounded-full border font-semibold ${
                        currentPage === i
                            ? 'bg-primary text-white'
                            : 'bg-white text-primary border-primary hover:bg-gray-50'
                    }`}
                >
                    {i}
                </button>,
            );
        }

        // Last page button
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageButtons.push(
                    <span key='ellipsis2' className='px-2 text-gray-500'>
                        ...
                    </span>,
                );
            }

            pageButtons.push(
                <button
                    key={totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className='px-4 py-2 rounded-full border font-semibold bg-white text-primary border-primary hover:bg-gray-50'
                >
                    {totalPages}
                </button>,
            );
        }

        return pageButtons;
    };

    return (
        <div className='bg-gray-100 min-h-screen'>
            <CarrierHeader />
            <div className='sm:ml-64 px-4 py-8 max-w-7xl mx-auto'>
                <h2 className='text-3xl font-bold text-primary mb-6'>Assigned Drivers</h2>

                <input
                    type='text'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder='Search by name, address or assigned Date'
                    className='mb-6 px-4 py-2 border border-gray-300 rounded w-full max-w-md'
                />

                {loading ? (
                    <div className='text-center py-10 text-gray-500'>Loading...</div>
                ) : message ? (
                    <div className='text-center py-10 text-red-600 font-semibold'>{message}</div>
                ) : (
                    <div className='overflow-x-auto bg-white shadow rounded'>
                        <table className='min-w-full table-auto'>
                            <thead className='bg-gray-100'>
                                <tr>
                                    <th className='px-4 py-2 text-left'>Driver</th>
                                    <th className='px-4 py-2 text-left'>Experience</th>
                                    <th className='px-4 py-2 text-left'>Location</th>
                                    <th className='px-4 py-2 text-left'>Route</th>
                                    <th className='px-4 py-2 text-left'>Assigned Date</th>
                                    <th className='px-4 py-2 text-left'>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentDrivers.map((d, i) => (
                                    <tr key={i} className='border-t hover:bg-gray-50'>
                                        <td className='px-4 py-2'>{d.name}</td>
                                        <td className='px-4 py-2'>{d.experience}</td>
                                        <td className='px-4 py-2'>
                                            {d.city}, {d.state}
                                        </td>
                                        <td className='py-4 px-2'>
                                            {Array.isArray(d.route) ? d.route.join(', ') : d.route}
                                        </td>
                                        <td className='px-4 py-2'>
                                            {d.assignedAt
                                                ? new Date(d.assignedAt).toLocaleDateString('en-US', {
                                                      month: '2-digit',
                                                      day: '2-digit',
                                                      year: 'numeric',
                                                  })
                                                : 'N/A'}
                                        </td>

                                        <td className='px-4 py-2'>
                                            <button
                                                onClick={() => handleViewDriver(d)}
                                                className='bg-primary text-white px-4 py-1 rounded hover:bg-primary-dark'
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {!loading && !message && totalPages > 0 && (
                    <div className='flex justify-center mt-6'>
                        <nav className='flex items-center space-x-2 shadow-md px-2 py-2 rounded-lg bg-white'>
                            {/* Previous button */}
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className={`h-9 w-9 flex items-center justify-center rounded-full transition ${
                                    currentPage === 1
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700'
                                }`}
                            >
                                &laquo;
                            </button>

                            {/* Page Buttons */}
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                // Only show first, last, and pages around current page
                                if (
                                    page === 1 ||
                                    page === totalPages ||
                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                ) {
                                    return (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`h-9 w-9 rounded-full flex items-center justify-center text-base font-semibold transition ${
                                                currentPage === page
                                                    ? 'bg-primary text-white shadow-lg border border-primary'
                                                    : 'bg-gray-100 text-gray-900 hover:bg-blue-100 hover:text-blue-700'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    );
                                }

                                // Show ellipsis between gaps
                                if (page === 2 && currentPage > 3) {
                                    return (
                                        <span
                                            key='start-ellipsis'
                                            className='h-9 w-9 flex items-center justify-center text-gray-500 select-none'
                                        >
                                            ...
                                        </span>
                                    );
                                }

                                if (page === totalPages - 1 && currentPage < totalPages - 2) {
                                    return (
                                        <span
                                            key='end-ellipsis'
                                            className='h-9 w-9 flex items-center justify-center text-gray-500 select-none'
                                        >
                                            ...
                                        </span>
                                    );
                                }

                                return null;
                            })}

                            {/* Next button */}
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className={`h-9 w-9 flex items-center justify-center rounded-full transition ${
                                    currentPage === totalPages
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700'
                                }`}
                            >
                                &raquo;
                            </button>
                        </nav>
                    </div>
                )}
            </div>

            {/* MAIN DRIVER DETAIL MODAL*/}
            {isModalOpen && selectedDriver && (
                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                    <div className='bg-white w-full max-w-4xl mx-4 rounded-2xl shadow-xl p-6 relative max-h-[90vh] overflow-y-auto'>
                        {/* Close button — resets all modal-related state */}
                        <button
                            onClick={handleCloseModal}
                            className='absolute top-3 right-4 text-gray-600 hover:text-blue-600 text-2xl font-bold'
                        >
                            &times;
                        </button>

                        {/*Driver name + Add Favourite button */}
                        <div className='flex items-center justify-between mb-5 pb-4 border-b-2 border-[#3c9bf9] w-full pt-4'>
                            <h2 className='text-xl font-semibold text-gray-900'>{selectedDriver.name}</h2>

                            <button
                                onClick={handleOpenConfirm}
                                className='bg-[#3c9bf9] text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-lg transition duration-300'
                            >
                                <i className='fas fa-star' /> Add to Favorites
                            </button>
                        </div>

                        {/* 🆕 Feedback message shown after add to favourites attempt */}
                        {favouriteMessage && (
                            <div
                                className={`text-sm px-3 py-2 rounded mb-3 ${
                                    favouriteMessage.includes('already')
                                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                        : favouriteMessage.includes('wrong')
                                          ? 'bg-red-50 text-red-600 border border-red-200'
                                          : 'bg-green-50 text-green-600 border border-green-200'
                                }`}
                            >
                                {favouriteMessage}
                            </div>
                        )}
                        {/*Driver Detail fields grid*/}
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                            {[
                                [
                                    'Route Type:',
                                    Array.isArray(selectedDriver.route)
                                        ? selectedDriver.route.join(', ')
                                        : selectedDriver.route || 'N/A',
                                ],
                                ['Job Type:', selectedDriver.job || 'N/A'],
                                ['Experience:', selectedDriver.experience || 'N/A'],

                                [
                                    'Trailer Type:',
                                    Array.isArray(selectedDriver.trailer)
                                        ? selectedDriver.trailer.join(', ')
                                        : selectedDriver.trailer || 'N/A',
                                ],
                                ['CDL Type:', selectedDriver.cdl || 'N/A'],
                                [
                                    'Endorsements:',
                                    Array.isArray(selectedDriver.endorsements)
                                        ? selectedDriver.endorsements.join(', ')
                                        : selectedDriver.endorsements || 'N/A',
                                ],
                                ['Contact:', selectedDriver.phone || 'N/A'],
                                ['Email:', selectedDriver.email || 'N/A'],
                                ['Location:', `${selectedDriver.city || ''}, ${selectedDriver.state || ''}`],
                                ['Address:', selectedDriver.address || 'N/A'],
                                // Assigned Date exists only in this page's context
                                [
                                    'Assigned Date:',
                                    selectedDriver.assignedAt
                                        ? new Date(selectedDriver.assignedAt).toLocaleDateString('en-US', {
                                              month: '2-digit',
                                              day: '2-digit',
                                              year: 'numeric',
                                          })
                                        : 'N/A',
                                ],
                            ].map(([label, value], index) => (
                                <div
                                    key={index}
                                    className='flex items-center p-3 bg-[#3c9bf9]/5 rounded-lg hover:bg-[#3c9bf9]/10 transition duration-300'
                                >
                                    <span className='w-32 font-semibold text-[#3c9bf9] text-sm'>{label}</span>
                                    <span className='text-gray-900 text-sm'>{value}</span>
                                </div>
                            ))}
                        </div>
                        {/* RESUME SECTION */}
                        <div className='mt-6'>
                            <h3 className='text-lg font-semibold text-gray-900 mb-2'>Resume</h3>
                            {resume ? (
                                <a
                                    href={`${process.env.REACT_APP_IMAGE_BASE_URL}/${resume.resume.replace(/\\/g, '/')}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='bg-blue-600 hover:bg-black text-white px-4 py-2 rounded text-sm'
                                >
                                    View Resume
                                </a>
                            ) : (
                                <button
                                    disabled
                                    className='bg-black text-white px-4 py-2 rounded text-sm cursor-not-allowed'
                                >
                                    No Resume Uploaded
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Add to favorites confirmation modal */}
            {isConfirmOpen && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                    <div className='bg-white rounded-lg shadow-xl p-6 max-w-sm w-full text-center'>
                        <h2 className='text-xl font-semibold mb-4 text-gray-800'>Add this driver to your favorites?</h2>
                        <div className='flex justify-center gap-4'>
                            <button
                                onClick={handleCloseConfirm}
                                className='bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded'
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmAddFavourite}
                                className='bg-[#3c9bf9] hover:bg-black text-white px-4 py-2 rounded'
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssignedDrivers;
